import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';

const client = createHttpClient(serviceUrls.gateway, { timeoutMs: 25_000 });

const quotaMeInflight = new Map<string, Promise<QuotaMeResponse>>();

export type SharedPoolSummary = {
  daily_limit_minutes: number | null;
  daily_used_minutes: number;
  daily_remaining_minutes: number | null;
  monthly_limit_minutes: number | null;
  monthly_used_minutes: number;
  monthly_remaining_minutes: number | null;
  daily_utilization_pct?: number | null;
  monthly_utilization_pct?: number | null;
};

export type SchoolAllocationRow = {
  feature_name: string;
  daily_minutes_allocated: number;
  monthly_minutes_allocated: number;
  daily_used_minutes: number;
  monthly_used_minutes: number;
  daily_remaining_minutes?: number | null;
  daily_utilization_pct?: number | null;
  monthly_utilization_pct?: number | null;
};

export type SchoolAllocationConfigMeta = {
  origin?: 'plan_defaults' | 'customized' | null;
  origin_at?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
};

export type SchoolAllocationChangeAuditRow = {
  school_id: string;
  changed_by: string;
  action: string;
  previous_daily_allocations: Record<string, number>;
  new_daily_allocations: Record<string, number>;
  previous_monthly_allocations: Record<string, number>;
  new_monthly_allocations: Record<string, number>;
  governance_mode_before: string;
  governance_mode_after: string;
  timestamp: string;
};

export type SchoolAiAllocationsResponse = {
  mode: 'flexible' | 'strict';
  shared_pool: SharedPoolSummary;
  allocations: SchoolAllocationRow[];
  config_meta?: SchoolAllocationConfigMeta;
  recent_changes?: SchoolAllocationChangeAuditRow[];
};

export type TeacherSchoolGovernanceSnapshot = SchoolAiAllocationsResponse & {
  live_ai_quota_pool_enabled?: boolean;
  /** Synced tenant_settings.feature_flags (plan entitlements). */
  feature_flags?: Record<string, boolean>;
  /** Plan-enabled live tutor governance features for this school. */
  enabled_features?: string[];
};

export type StudentPreferredAllocation = {
  display_feature: string;
  daily_minutes_allocated: number;
  daily_used_minutes: number;
  daily_used_seconds?: number;
  daily_remaining_minutes?: number | null;
  daily_remaining_seconds?: number | null;
  monthly_minutes_allocated?: number;
  monthly_used_minutes?: number;
  monthly_remaining_minutes?: number | null;
};

export type StudentQuotaMeResponse = {
  ok?: boolean;
  feature_name: string;
  daily_allocated_seconds: number | null;
  daily_used_seconds: number;
  daily_remaining_seconds: number | null;
  monthly_allocated_seconds?: number | null;
  monthly_used_seconds?: number;
  monthly_remaining_seconds?: number | null;
  quota_exhausted?: boolean;
  /** Pooled ledger scope when school live AI pool is enabled. */
  usage_scope?: 'pooled' | 'per_feature' | string;
  governance_mode?: 'flexible' | 'strict';
  feature_disabled?: boolean;
  channel_daily_used_seconds?: number;
  channel_daily_limit_seconds?: number | null;
  channel_monthly_used_seconds?: number;
  channel_monthly_limit_seconds?: number | null;
  /** Subscription pool allocation (minutes) when school uses shared live AI pool. */
  preferred_allocation?: StudentPreferredAllocation | null;
  shared_pool?: SharedPoolSummary | null;
  /** Student-wide live AI pool usage (all tutors combined) when usage_scope is pooled. */
  live_pool_daily_used_seconds?: number;
  live_pool_daily_allocated_seconds?: number | null;
  live_pool_daily_remaining_seconds?: number | null;
  /** Legacy minute fields from older backends (prefer seconds). */
  daily_remaining_minutes?: number | null;
  daily_used_minutes?: number;
  daily_limit_minutes?: number | null;
  monthly_used_minutes?: number;
  monthly_remaining_minutes?: number | null;
};

export type QuotaMeResponse = StudentQuotaMeResponse;

export async function fetchSchoolLivePoolSummary(): Promise<SharedPoolSummary> {
  const res = await client.get<{ ok?: boolean } & SharedPoolSummary>('/v1/tutor/quota/school-live-pool-summary');
  const { ok: _ok, ...rest } = res.data;
  return rest;
}

export async function fetchSchoolAiAllocations(schoolId?: string): Promise<SchoolAiAllocationsResponse> {
  const params = schoolId ? { school_id: schoolId } : undefined;
  const res = await client.get<SchoolAiAllocationsResponse>('/v1/tutor/school-ai-allocations', { params });
  return res.data;
}

export async function patchSchoolAiAllocations(body: {
  mode?: 'flexible' | 'strict';
  action?: 'save' | 'reset_to_plan_defaults';
  allocations: Array<{
    feature_name: string;
    daily_minutes_allocated: number;
    monthly_minutes_allocated: number;
  }>;
}): Promise<SchoolAiAllocationsResponse> {
  const res = await client.patch<SchoolAiAllocationsResponse>('/v1/tutor/school-ai-allocations', body);
  return res.data;
}

/**
 * @deprecated Quota Runtime V1 — student Start Class snapshot via ``/v1/tutor/quota/me``.
 * KEEP while ``quota_runtime_v2_enabled`` is false.
 * Under V2, Start Class uses ``fetchRuntimeQuota`` / ``startRuntimeSession``.
 * Removal phase: after V2 is default (endpoint may remain for debug/admin).
 */
export async function fetchQuotaMe(featureName: string): Promise<QuotaMeResponse> {
  const key = (featureName || '').trim() || 'ai_lesson_tutor';
  const existing = quotaMeInflight.get(key);
  if (existing) return existing;

  const run = client
    .get<QuotaMeResponse>('/v1/tutor/quota/me', {
      params: { feature_name: key },
    })
    .then((res) => res.data);

  quotaMeInflight.set(key, run);
  try {
    return await run;
  } finally {
    if (quotaMeInflight.get(key) === run) {
      quotaMeInflight.delete(key);
    }
  }
}

export async function fetchSchoolAiGovernanceAnalytics(): Promise<SchoolAiAllocationsResponse> {
  const res = await client.get<SchoolAiAllocationsResponse>('/v1/tutor/insights/school/ai-governance');
  return res.data;
}

export async function fetchTeacherSchoolGovernanceSnapshot(): Promise<TeacherSchoolGovernanceSnapshot> {
  const res = await client.get<TeacherSchoolGovernanceSnapshot & { ok?: boolean }>(
    '/v1/tutor/teacher/school-governance-snapshot'
  );
  return res.data;
}
